/**
 * Board module mutations
 * @packageDocumentation
 * @category Store
 */
import { MutationTree } from 'vuex';
import { BoardState as State } from '@/types';
import getDefaultBoardState from './getDefaultBoardState';
import { isTokenExpired } from '@/utilities/jwtUtilities';
import {
  getQuickCardObjectName,
  getQuickCardFilesToUpdateName,
} from '@/utilities/getQuickCardObjectName';
import { convertChildCardDraftToChildCard } from '@/utilities/childCardDraft';
import { nanoid } from 'nanoid';
import { DisplayedCard } from '@/types/displayedCard';
import constants from '@/utilities/constants';
import { EpicTabId } from '@/types/tabs';
import { ResourceType } from '@/types/resources';
import { getSprintKey } from '@/utilities/sprintKeys';
import { groupByOption } from '@/types/groupByOption';
import { toRaw } from 'vue';
import Fuse from 'fuse.js';
import { browserStorageSetItem, browserStorageGetItem } from '@/utilities/browser-storage.util';
import {
  listsExpandedKey,
  cardsSubGroupByOrderKey,
  boardSubGroupByKey,
  groupByListOrderKey,
  groupByCardOrderKey,
} from '@/utilities/localDrafts';
import isEqual from 'lodash/isEqual';

const setCardsFuse = (state: State) => {
  state.cardsFuse = new Fuse(Object.values(state.cards), {
    keys: ['title', 'board_id'],
    threshold: 0.1,
    ignoreLocation: true,
  });
};

const mutations: MutationTree<State> = {
  setCurrentBoardId(state, boardId = '') {
    state.currentBoardId = boardId;
    if (boardId !== '') state.lastOpenedBoardId = boardId;
  },

  setBoardListsFlat(state, lists = []) {
    lists?.forEach((l: any) => {
      if (l) state.lists[l.id] = l;
    });
  },

  addBoardListId(state, { boardId, listId, position }) {
    if (!state.boardsListsIds[boardId]) {
      state.boardsListsIds[boardId] = [listId];
      return;
    }
    if (position != null) {
      state.boardsListsIds[boardId].splice(position, 0, listId);
      return;
    }
    state.boardsListsIds[boardId].push(listId);
  },

  replaceBoardListId(state, { boardId, localId, listId }) {
    const index = state.boardsListsIds[boardId].indexOf(localId);
    state.boardsListsIds[boardId].splice(index, 1, listId);
  },

  moveBoardListId(state, { boardId, listId, newIndex, oldIndex }) {
    if (state.boardsListsIds[boardId]) {
      state.boardsListsIds[boardId].splice(oldIndex, 1);
      state.boardsListsIds[boardId].splice(newIndex, 0, listId);
    }
  },

  removeBoardListId(state, { boardId, id }) {
    const index = state.boardsListsIds[boardId].indexOf(id);
    state.boardsListsIds[boardId].splice(index, 1);
  },

  removeBoardList(state, listId) {
    delete state.lists[listId];
  },

  removeMultipleBoardLists(state, listIds) {
    listIds.forEach((listId: any) => {
      delete state.lists[listId];
    });
  },

  addBoardInBoards(state, board) {
    if (state.projectBoards[board.project_id]) {
      state.projectBoards[board.project_id].push(board.id);
    }
    state.flatBoards[board.id] = board;
  },

  removeBoardFromProjectBoards(state, board) {
    const projectBoards = state.projectBoards || {};
    const boardArray = projectBoards[board.project_id] || [];
    const boardIndex = boardArray.indexOf(board.id);
    if (boardIndex > -1) {
      projectBoards[board.project_id].splice(boardIndex, 1);
    }
    state.projectBoards = projectBoards;
  },

  setBoardsListsIds(state, boards) {
    for (const b of boards) {
      if (b.list_order && !state.boardsListsIds[b.id]?.length) {
        state.boardsListsIds[b.id] = b.list_order;
      }
    }
  },

  setSprintsListsIds(state, { sprintId, list, projectId }) {
    // set sprint lists ids with board list ids together
    state.boardsListsIds[getSprintKey(projectId, sprintId)] = list;
  },

  setReportInfoData(state, { boardId, report }) {
    state.reportInfoData[boardId] = report;
  },

  setNewBoardProjectId(state, name) {
    state.newBoardProjectId = name;
  },

  setListDraftCardCreateView(state, listId) {
    state.listDraftCardCreateView = listId;
  },

  setRemovedBoardListPosition(state, { position, boardId, listId }) {
    state.removedListMeta = { position, boardId, listId };
  },

  setRemovedCardPosition(state, { position, cardId, boardId, listId }) {
    state.removedCardMeta = {
      position,
      cardId,
      boardId,
      listId,
    };
  },

  setTimeperoidOptions(state, value) {
    state.timeperiodOptions = value;
  },

  setDraggedEntity(state, payload) {
    state.draggedEntity = payload;
  },
  setDropTargetId(state, payload) {
    state.dropTargetId = payload;
  },

  toggleBoardQuickView(state, { boardId, value }) {
    state.expandedBoardIds[boardId] = value;
  },

  toggleProjectBoards(state, { value, projectId }) {
    const { projectBoards, expandedBoardIds } = state;
    const boards = projectBoards[projectId] || [];
    if (boards.length === 0) return;
    const newExpandedBoardIds = { ...expandedBoardIds };
    for (const boardId of boards) {
      if (!expandedBoardIds[boardId]) return;
      newExpandedBoardIds[boardId] = value;
    }
    state.expandedBoardIds = newExpandedBoardIds;
  },
  toggleBoardsInRow(state, { boardId, value, projectId }) {
    const { projectBoards, expandedBoardIds } = state;
    const boards = projectBoards[projectId] || [];
    if (boards.length === 0) return;

    const boardIndex = boards.findIndex((board: any) => board === boardId);
    if (boardIndex === -1) return;

    const otherBoardIndex = boardIndex % 2 === 0 ? boardIndex + 1 : boardIndex - 1;
    if (otherBoardIndex >= boards.length) return;

    const newExpandedBoardIds = { ...expandedBoardIds };
    const otherBoardId = boards[otherBoardIndex];

    newExpandedBoardIds[boardId] = value;
    newExpandedBoardIds[otherBoardId] = value;
    state.expandedBoardIds = newExpandedBoardIds;
  },

  loadingBoards(state, payload = false) {
    state.loadingBoards = payload;
  },

  setBoardListProp(state, { listId, propertyName, value }) {
    if (!state.lists[listId]) return;
    state.lists[listId][propertyName] = value;
  },

  setBoardViewNoDestroy(state, payload = false) {
    state.boardViewNoDestroy = payload;
  },

  setDraftListInputVisibleOnOpen(state, payload = false) {
    state.draftListInputVisibleOnOpen = payload;
  },

  localAddMemberToBoard(state, { memberToAdd, boardId }) {
    const board = state.flatBoards[boardId];

    if (board) {
      const members = [...board.members, memberToAdd];
      state.flatBoards[boardId].members = members;
    }
  },

  localRemoveMemberFromBoard(state, { memberToRemove, boardId }) {
    const board = state.flatBoards[boardId];

    if (board) {
      const members = [...board.members.filter((m: any) => m.user_id !== memberToRemove.user_id)];
      state.flatBoards[boardId].members = members;
    }
  },

  localAddMemberToCard(state, { cardId, memberToAdd }) {
    // add memeber to card if listCards object is not empty(if board view is opened)
    if (state.cards[cardId]) {
      const { members } = state.cards[cardId];
      const newMembers = [...(members || [])];
      if (members) {
        const alradyMember = members.find((m: any) => m.user_id === memberToAdd.user_id);
        if (alradyMember) return;
        newMembers.push(memberToAdd);
        state.cards[cardId].members = newMembers;
      }
    }
  },

  localRemoveMemberFromCardBoardView(state, { cardId, memberId }) {
    if (state.cards[cardId]) {
      const { members } = state.cards[cardId];
      const memberIndex = members.findIndex((m: any) => m.user_id === memberId);
      const newMembers = [...members];
      if (memberIndex > -1) {
        newMembers.splice(memberIndex, 1);
        state.cards[cardId].members = newMembers;
      }
    }
  },

  setBoardListView(state, value) {
    state.boardListView = value;
  },

  setCurrentTagForEdit(state, tagForEdit) {
    state.currentTagForEdit = tagForEdit;
  },

  localAddTagToCard(state, { cardId, tag }) {
    if (state.cards[cardId]) {
      if (!state.cards[cardId].tags) state.cards[cardId].tags = [];
      const alradyTag = state.cards[cardId].tags.find((t: any) => t.id === tag.id);
      if (alradyTag) return;
      state.cards[cardId].tags.push(tag);
    }
  },
  updateLocalCardTag(state, { cardId, tagId, tag }) {
    if (state.cards[cardId]) {
      const newCardTags = [...state.cards[cardId].tags];
      const listCardTagIndex = newCardTags.findIndex((t: any) => t.id === tagId);
      if (listCardTagIndex !== -1) {
        newCardTags.splice(listCardTagIndex, 1, tag);
        state.cards[cardId].tags = newCardTags;
      }
    }
  },
  localAddTagToBoard(state, { boardId, tag }) {
    if (state.flatBoards[boardId]) {
      const board = state.flatBoards[boardId];

      if (!board.tags) {
        state.flatBoards[boardId].tags = [];
      }

      board.tags.push(tag);
    }
  },
  localDeleteTagFromCard(state, { cardId, tagId }) {
    if (state.cards[cardId]) {
      if (state.cards[cardId].tags) {
        const tags = [...state.cards[cardId].tags];
        const tagIndex = tags.findIndex((t: any) => t.id === tagId);
        if (tagIndex > -1) {
          tags.splice(tagIndex, 1);
          state.cards[cardId].tags = tags;
        }
      }
    }
  },
  localDeleteTagFromCards(state, { tagId }) {
    Object.keys(state.cards).forEach((cardId: any) => {
      if (state.cards[cardId].tags) {
        const tags = [...state.cards[cardId].tags];
        const tagIndex = tags.findIndex((t: any) => t.id === tagId);
        if (tagIndex > -1) {
          tags.splice(tagIndex, 1);
          state.cards[cardId].tags = tags;
        }
      }
    });
  },
  setLinkedCardType(state, value) {
    state.linkedCardType = value;
  },
  setShowImageUploadPopup(state, value) {
    state.showImageUploadPopup = value;
  },
  setCurrentCardIdForActionEdit(state, cardId) {
    state.currentCardIdActionEdit = cardId;
  },
  setCurrentListIdForActionEdit(state, listId) {
    state.currentListIdActionEdit = listId;
  },
  setCardsLayout(state, { boardId, value }) {
    state.cardsLayout[boardId] = value;
  },
  replaceLocalChildCardIdInParentCard(state, { parentCardId, localId, newId }) {
    const parentCard = state.cards[parentCardId];
    if (!parentCard) return;

    // update card in parents child card order
    const childCardOrder = [...(parentCard.child_card_order || [])];
    const childCardOrderIndex = childCardOrder.findIndex((i: any) => i === localId);

    if (childCardOrderIndex > -1) {
      childCardOrder.splice(childCardOrderIndex, 1, newId);
      parentCard.child_card_order = childCardOrder;
    }

    // update card in parents child cards
    const childCards = [...(parentCard.child_cards || [])];
    const childCardIndex = childCards.findIndex((c: any) => c.local_id || c.card_id === localId);

    if (childCardIndex > -1) {
      const updatedChildCard = {
        ...childCards[childCardIndex],
        card_id: newId,
      };
      delete updatedChildCard.local_id;
      delete updatedChildCard.localCard;

      childCards.splice(childCardIndex, 1, updatedChildCard);
      parentCard.child_cards = childCards;
    }
  },

  localUpdateCardFields(
    state,
    { cardId, card, overwrite = false, timeUpdated = null, userIdUpdated = null }
  ) {
    if (state.cards[cardId]) {
      const newCard = overwrite ? card : { ...state.cards[cardId], ...card };
      if (timeUpdated) {
        newCard.time_updated = timeUpdated;
      }
      if (userIdUpdated) {
        newCard.user_id_updated = userIdUpdated;
      }
      state.cards[cardId] = newCard;
    }

    // Update Fuse index if title or board_id has changed
    if ('title' in card || 'board_id' in card) {
      setCardsFuse(state);
    }
  },

  localUpdateCardField(
    state,
    { cardId, fieldName, fieldValue, timeUpdated = null, userIdUpdated = null }
  ) {
    if (!state.cards?.[cardId]) return;
    state.cards[cardId][fieldName] = fieldValue;
    if (timeUpdated && userIdUpdated) {
      state.cards[cardId].time_updated = timeUpdated;
      state.cards[cardId].user_id_updated = userIdUpdated;
    }

    // Update Fuse index when title or other searchable fields change
    if (fieldName === 'title' || fieldName === 'board_id') {
      setCardsFuse(state);
    }
  },

  localUpdateCardFieldInMultipleCards(state, { cardIds, fieldName, fieldValue }) {
    cardIds.forEach((cardId: string | number) => {
      if (!state.cards[cardId]) return;
      state.cards[cardId][fieldName] = fieldValue;
    });
  },

  setAttachments(state, { files, id, prefix }) {
    const resourceId = `${prefix}-${id}`;
    state.attachments[resourceId] = files;
  },

  localAddAttachment(state, { attachment, id, prefix }) {
    const resourceId = `${prefix}-${id}`;
    if (!state.attachments[resourceId]) {
      state.attachments[resourceId] = [];
    }
    state.attachments[resourceId].splice(0, 0, attachment);
  },

  localRemoveAttachment(state, { attachmentId, id, prefix }) {
    const resourceId = `${prefix}-${id}`;
    const newAttachments = [...(state.attachments[resourceId] || [])];
    const index = newAttachments.findIndex((a: any) => a.local_id === attachmentId);

    if (index > -1) {
      newAttachments.splice(index, 1);
      state.attachments[resourceId] = newAttachments;
    }
  },

  addAttachmentUploadDetails(state, { id, localId, uploadDetails }) {
    const resourceId = `c-${id}`;
    const newAttachments = [...(state.attachments[resourceId] || [])];
    const attachmentIndex = newAttachments.findIndex(
      (attachment: any) => attachment.local_id === localId
    );

    if (attachmentIndex > -1) {
      newAttachments.splice(attachmentIndex, 1, {
        ...newAttachments[attachmentIndex],
        ...uploadDetails,
      });
      state.attachments[resourceId] = newAttachments;
    }
  },

  localRemoveFileFromCard(state, { fileId, cardId }: { fileId: string; cardId: string }) {
    const cId = `c-${cardId}`;
    const newCardAttachments = [...state.attachments[cId]];

    const fileIndex = newCardAttachments.findIndex((f: any) => f.id === fileId);
    if (fileIndex !== -1) {
      newCardAttachments.splice(fileIndex, 1);
      state.attachments[cId] = newCardAttachments;
    }
  },

  updateCardAttachmentId(state, { attachment, localId, cardId }) {
    const [file] = attachment;

    const resourceId = `c-${cardId}`;
    const newAttachemnts = [...state.attachments[resourceId]];
    const index = newAttachemnts.findIndex((a) => a.local_id === localId);
    if (index !== -1) {
      newAttachemnts.splice(index, 1, file);
      state.attachments[resourceId] = newAttachemnts;
    }
  },

  setUploadFailedFlag(state, { localId, file, cardId, value = true }) {
    const attachmentsKey = `c-${cardId}`;

    const newAttachemnts = [...state.attachments[attachmentsKey]];
    const index = newAttachemnts.findIndex((a) => a.local_id === localId);
    if (index !== -1) {
      const newFile = {
        ...newAttachemnts[index],
        failed: value,
        file,
      };
      newAttachemnts.splice(index, 1, newFile);
      state.attachments[attachmentsKey] = newAttachemnts;
    }
  },

  addCardFailedAttachment(state, attachment) {
    state.cardFailedAttachments.push(attachment);
  },

  resetBoardState(state) {
    Object.assign(state, getDefaultBoardState());
  },

  setCardAutoFocus(state, payload) {
    state.cardAutoFocus = payload;
  },

  setPreviewPopoverKey(state, payload) {
    state.previewPopoverKey = payload;
  },

  setAttachmentTargetCardId(state, id) {
    state.attachmentTargetCardId = id;
  },

  setCardAttachmentsLoaded(state, value) {
    state.cardAttachmentsLoaded = value;
  },
  setArchivedBoards(state, data = []) {
    state.archivedBoards = data;
  },

  setArchivedLists(state, data = []) {
    state.archivedLists = data;
  },

  setProjectPreviewBoards(state, { projectBoards = [], projectId }) {
    state.projectPreviewBoards[projectId] = projectBoards;
  },

  setArchivedCards(state, data = []) {
    state.archivedCards = data;
  },

  addArchivedListToIndex(state, { list, index }) {
    state.archivedLists.splice(index, 0, list);
  },

  setNewBoardTitle(state, title) {
    state.newBoardTitle = title;
  },

  clearProjectPreviewBoards(state) {
    state.projectPreviewBoards = {};
  },

  setProjectPreview(state, project = {}) {
    state.previewProject = project;
  },

  displayProjectPreviewBoards(state, payload = false) {
    state.displayProjectPreviewBoards = payload;
  },

  setCurrentCardIdMemberDropdown(state, cardId) {
    state.currentCardIdMemberDropdown = cardId;
  },

  setCurrentCardMembersDropdown(state, members) {
    state.currentCardMembersDropdown = members;
  },

  setCurrentCardIdForPopup(state, cardId) {
    state.currentCardIdForPopup = cardId;
  },

  setCardAttachmentUploadingId(state, cardId) {
    state.cardAttachmentUploadingId = cardId;
  },

  setShowDuplicateCardPopup(state, payload) {
    state.showDuplicateCardPopup = payload;
  },

  setShowSetParentCardPopup(state, payload) {
    state.showSetParentCardPopup = payload;
  },

  updateBoardPropertyInBoards(state, { boardId, propertyName, value }) {
    if (state.flatBoards[boardId]) {
      state.flatBoards[boardId][propertyName] = value;
    }
    if (state.currentBoardId === boardId) {
      state.currentBoard = value;
    }
  },

  setDraggingEnd(state, value) {
    state.draggingEnd = value;
  },

  setProjectBoards(state, { projectId, projectBoards }) {
    state.projectBoards[projectId] = projectBoards;
  },

  removeProjectBoard(state, { projectId, boardId }) {
    if (state.projectBoards[projectId]) {
      const index = state.projectBoards[projectId].indexOf(boardId);
      state.projectBoards[projectId].splice(index, 1);
    }
  },

  addProjectBoard(state, { projectId, boardId, position = -1 }) {
    if (position > -1) {
      state.projectBoards[projectId].splice(position, 0, boardId);
      return;
    }
    state.projectBoards[projectId].push(boardId);
  },

  setFlatBoards(state, boardsArray) {
    for (const board of boardsArray) {
      const fullyLoaded =
        state.flatBoards[board.id] &&
        (state.flatBoards[board.id].lists || []).length &&
        (state.flatBoards[board.id].lists || [])[0].cards != null;
      if (!fullyLoaded) {
        state.flatBoards[board.id] = board;
      }
    }
  },

  setCards(state, cards) {
    for (const cardId in cards) {
      const existingCard = state.cards[cardId];
      const newCard = cards[cardId];

      if (!existingCard) {
        state.cards[cardId] = newCard;
        continue;
      }

      state.cards[cardId] = {
        ...existingCard,
        ...newCard,
      };
    }

    setCardsFuse(state);
  },

  setListCards(state, listCards) {
    for (const listId in listCards) {
      if (
        Object.hasOwn(listCards, listId) &&
        !isEqual(state.listCards[listId], listCards[listId])
      ) {
        state.listCards[listId] = listCards[listId];
      }
    }
  },

  setSingleListInListCards(state, { listId, value }) {
    state.listCards[listId] = value;
  },

  addCardToListCards(state, { listId, cardId }) {
    state.listCards[listId] ??= [];
    if (!state.listCards[listId].includes(cardId)) {
      state.listCards[listId].push(cardId);
    }
  },

  removeCardFromListCards(state, { listId, cardId }) {
    if (state.listCards[listId]) {
      const index = state.listCards[listId].findIndex((id: any) => id === cardId);
      if (index > -1) {
        state.listCards[listId].splice(index, 1);
      }
    }
  },

  addCardToCards(state, { card }) {
    const oldCard = state.cards[card.id] ?? {};
    let expired = true;
    if (oldCard?.collaboration?.token) {
      expired = isTokenExpired(oldCard.collaboration.token);
    }

    // Check if new card has more keys than old card
    const oldCardKeys = Object.keys(oldCard);
    const newCardKeys = Object.keys(card);
    const hasMoreKeys = newCardKeys.length > oldCardKeys.length;

    // Check if new card is newer than old card
    const isNewer =
      !oldCard.time_updated || (card.time_updated && card.time_updated > oldCard.time_updated);

    // Only update card if:
    // 1. New card is newer than old card
    // 2. Collaboration token is expired
    // 3. New card has more keys (this also handles case when no existing card is present)
    if (isNewer || expired || hasMoreKeys) {
      const newCard = {
        ...card,
        collaboration: expired ? toRaw(card.collaboration) : toRaw(oldCard.collaboration),
      };
      state.cards[card.id ?? card.localCardId] = newCard;
    }
  },

  removeCardFromCards(state, cardId) {
    delete state.cards[cardId];
  },
  cardWsEventsUpdate(state, { change, cardId }) {
    const [cardProp, cardPropId, secondResource, thirdResource, fourthResource] = change.path;
    const card = state.cards[cardId];
    let cardIndex = -1;
    let cardTagIndex = -1;
    let checklistIndex = -1;
    if (Array.isArray(card[cardProp])) {
      cardIndex = card[cardProp].findIndex((c: any) => c.card_id === cardPropId);
    }

    switch (cardProp) {
      case 'hints':
        card.hints = change.to;
        break;
      case 'child_card_order':
        card.child_card_order = change.to || [];
        break;
      case 'tags':
        cardTagIndex = card.tags.findIndex((tag: any) => tag.id === cardPropId);
        if (card.tags[cardTagIndex]) {
          card.tags[cardTagIndex][secondResource] = change.to;
        }
        if (state.tags[cardPropId]) {
          state.tags[cardPropId][secondResource] = change.to;
          const newTags = { ...state.tags };
          state.tags = newTags;
        }
        break;
      case 'checklists':
        checklistIndex = card.checklists.findIndex((c: { id: any }) => c.id === cardPropId);
        if (change.path.includes('items')) {
          const itemId = thirdResource;
          const itemIndex = card.checklists[checklistIndex].items.findIndex(
            (item: any) => item.id === itemId
          );
          card.checklists[checklistIndex].items[itemIndex][fourthResource] = change.to;
          break;
        }
        card.checklists[checklistIndex][secondResource] = change.to;
        break;
      case 'checklist_item_order':
        card.checklist_item_order[cardPropId] = change.to;
        break;
      case 'icon':
      case 'epic':
      case 'parent_card':
        // title has been updated
        if (cardProp && cardPropId) {
          state.cards[cardId][cardProp][cardPropId] = change.to;
          break;
        }

        // parent/card removed
        if (change.from && !change.to) {
          delete state.cards[cardId][cardProp];
          break;
        }

        // parent/epic added
        if (change.to) {
          state.cards[cardId][cardProp] = change.to;
          break;
        }

        break;
      case 'linked_cards':
        if (card[cardProp][cardIndex]?.[secondResource]) {
          card[cardProp][cardIndex][secondResource] = change.to;
        }
        if (state.cards[cardPropId]?.[secondResource]) {
          state.cards[cardPropId][secondResource] = change.to;
        }
        break;
      case 'child_cards':
        if (card[cardProp][cardIndex]?.[secondResource]) {
          card[cardProp][cardIndex][secondResource] = change.to;
        }
        if (state.cards[cardPropId]?.[secondResource]) {
          state.cards[cardPropId][secondResource] = change.to;
        }
        if (secondResource === 'archived') {
          if (change.to) {
            state.cards[cardPropId].archived = change.to;
          } else {
            delete state.cards[cardPropId].archived;
          }
        }
        break;
      case 'external_links':
        break;
      default:
        // check if the property is a card property
        if (card[cardProp] !== undefined) {
          card[cardProp] = change.to;
        } else {
          const newCard = { ...state.cards[cardId], [cardProp]: change.to };
          state.cards[cardId] = newCard;
        }
        break;
    }
    // Update Fuse index if title was updated via websocket
    if (cardProp === 'title' || cardProp === 'board_id') {
      setCardsFuse(state);
    }
  },

  cardWsEventsCreate(state, { change, cardId, projectId, teamId }) {
    const [cardProp, cardPropId] = change.path;
    const card = state.cards[cardId];
    switch (cardProp) {
      case 'hints':
        card.hints = change.to;
        break;
      case 'checklists':
        if (change.path.includes('items')) {
          const checklistIndex = card.checklists.findIndex((c: { id: any }) => c.id === cardPropId);
          card.checklists[checklistIndex]?.items.push(change.to);
        } else if (change.to.id) {
          card.checklists.push(change.to);
        }
        break;
      case 'checklist_item_order':
        card.checklist_item_order[cardPropId] = change.to;
        break;
      case 'start_date':
      case 'due_date':
      case 'child_card_order':
        card[cardProp] = change.to;
        break;
      case 'tags':
        if (card[cardProp].findIndex((tag: any) => tag.id === change.to.id) === -1) {
          card[cardProp].push(change.to);
        }
        state.tags[change.to.id] = change.to;
        if (
          !state.projectTags[projectId]?.includes(change.to.id) &&
          !state.workspaceTags[teamId]?.includes(change.to.id)
        ) {
          state.projectTags[projectId]?.push(change.to.id);
        }
        break;
      case 'external_links':
        break;
      default:
        if (Array.isArray(card[cardProp])) {
          card[cardProp].push(change.to);
        }
        break;
    }
  },

  cardWsEventsDelete(state, { change, cardId }) {
    const [cardProp, cardPropId, , thirdResource] = change.path;
    const card = state.cards[cardId];
    switch (cardProp) {
      case 'hints':
        if (card.hints[cardPropId]) {
          card.hints.splice(cardPropId, 1);
        }
        break;
      case 'child_card_order':
        card.child_card_order = change.to || [];
        break;
      case 'tags':
        const tagId = change.from.id;
        const index = card.tags.findIndex((tag: any) => tag.id === tagId);
        card.tags.splice(index, 1);
        break;
      case 'checklists':
        if (change.path.includes('items')) {
          const itemId = thirdResource;
          if (card.checklists) {
            const checklistIndex = card.checklists.findIndex(
              (c: { id: any }) => c.id === cardPropId
            );
            const itemIndex = card.checklists[checklistIndex].items?.findIndex(
              (item: any) => item.id === itemId
            );
            card.checklists[checklistIndex].items?.splice(itemIndex, 1);
          }
          break;
        }
        const checklistId = change.from.id;
        const checklistIndex = card.checklists.findIndex(
          (checklist: any) => checklist.id === checklistId
        );
        card.checklists.splice(checklistIndex, 1);
        break;
      case 'members':
        const memberIndex = card.members.findIndex((member: any) => member.user_id === cardPropId);
        card.members.splice(memberIndex, 1);
        break;
      case 'start_date':
        card.start_date = 0;
        break;
      case 'due_date':
        card.due_date = 0;
        break;
      case 'child_cards':
      case 'linked_cards':
        const cardIndex = card[cardProp].findIndex((c: any) => c.card_id === cardPropId);
        card[cardProp].splice(cardIndex, 1);
        break;
      case 'external_links':
        break;
    }
  },

  /**
   * Intended behavior:
   * 1. By passing an empty array, we will clear the displayed cards.
   * 2. Cards passed in the payload will be displayed, overriding the currently displayed cards.
   * 3. If the payload contains an epic:
   *  - The epic will be displayed, overriding the currently displayed epic.
   *  - In case when there are multiple epics in the payload, the first one will be displayed.
   *  - If there are no cards in the payload, only cards currently displayed in docked layout will be retained.
   * 4. If the payload does not contain an epic:
   *  - Currently displayed epic will be retained.
   */
  setDisplayedCards(state, payload: DisplayedCard[]) {
    if (!payload.length) {
      state.displayedCards = [];
      return;
    }

    let epicToBeDisplayed = payload.find(
      (dc: DisplayedCard) => dc.resource_type === ResourceType.Epic
    );

    if (!epicToBeDisplayed) {
      epicToBeDisplayed = state.displayedCards.find(
        (dc: DisplayedCard) => dc.resource_type === ResourceType.Epic
      );
    }

    const cardsToBeDisplayed: DisplayedCard[] = [];

    payload.forEach((dc: DisplayedCard) => {
      if (dc.resource_type !== ResourceType.Epic) {
        cardsToBeDisplayed.push(dc);
      }
    });

    if (!cardsToBeDisplayed.length) {
      state.displayedCards.forEach((dc: DisplayedCard) => {
        if (
          dc.resource_type !== ResourceType.Epic &&
          (epicToBeDisplayed ? dc.layout === constants.docked : true)
        ) {
          cardsToBeDisplayed.push(dc);
        }
      });
    }

    state.displayedCards = [
      ...(epicToBeDisplayed ? [epicToBeDisplayed] : []),
      ...cardsToBeDisplayed,
    ];
  },

  addDisplayedCards(state, payload: DisplayedCard[]) {
    state.displayedCards.push(...payload);
  },

  removeDisplayedCards(state, payload: string[] | string) {
    state.displayedCards = state.displayedCards.filter((c) => !payload.includes(c.id));
  },

  removeDisplayedCardsByField(
    state,
    payload: { field: keyof DisplayedCard; value: string; shouldMatch: boolean }
  ) {
    state.displayedCards = state.displayedCards.filter(
      (c: DisplayedCard) => (payload.shouldMatch ?? true) !== (c[payload.field] === payload.value)
    );
  },

  setFieldOfDisplayedCard(state, payload: { id: string; value: any; field: keyof DisplayedCard }) {
    if (!payload.field) return;

    const idx = state.displayedCards.findIndex((c: DisplayedCard) => c.id === payload.id);
    if (idx === -1) return;

    const { field, value } = payload;
    state.displayedCards[idx][field] = value;
  },

  setFieldsOfDisplayedCard(
    state,
    { id, payload }: { id: string; payload: Array<{ field: keyof DisplayedCard; value: any }> }
  ) {
    const idx = state.displayedCards.findIndex((c: DisplayedCard) => c.id === id);
    if (idx === -1) return;

    payload.forEach(({ field, value }) => {
      state.displayedCards[idx][field] = value;
    });
  },

  setCurrentCardLayout(state, layout) {
    localStorage.setItem('cardLayout', layout);
    state.currentCardLayout = layout;
  },

  removeCardFromArchived(state, cardId) {
    const index = state.archivedCards.findIndex((c: any) => c.id === cardId);

    if (index > -1) {
      const newArchivedCards = [...state.archivedCards];

      newArchivedCards.splice(index, 1);
      state.archivedCards = newArchivedCards;
    }
  },

  setChecklistCollapsedIds(state, checklistIds) {
    state.collapsedChecklistIds = checklistIds;
  },

  setHiddenItemChecklistIds(state, checklistIds) {
    state.hiddenItemsChecklistIds = checklistIds;
  },

  setBoardFilters(state, { boardId, filters, teamId }) {
    state.boardsFilters[boardId] = filters;
    localStorage.setItem(`boardsFilters-${teamId}`, JSON.stringify(state.boardsFilters));
  },

  setBoardPills(state, { boardId, pills }) {
    state.boardsPills[boardId] = pills;
  },

  setTags(state, { tagsArray, teamId }) {
    for (const tag of tagsArray) {
      const existingTag = state.tags[tag.id];
      const hasTagChanged =
        !existingTag ||
        existingTag.name !== tag.name ||
        existingTag.color !== tag.color ||
        existingTag.total_cards !== tag.total_cards ||
        existingTag.team_id !== tag.team_id;

      if (hasTagChanged) {
        state.tags[tag.id] ??= tag;
        if (!isEqual(toRaw(state.tags[tag.id]), tag)) {
          state.tags[tag.id] = { ...state.tags[tag.id], ...tag };
        }

        if (tag.project_id) {
          state.projectTags[tag.project_id] ??= [tag.id];
          if (!state.projectTags[tag.project_id].includes(tag.id)) {
            state.projectTags[tag.project_id].push(tag.id);
          }
        } else {
          state.workspaceTags[teamId] ??= [tag.id];
          if (!state.workspaceTags[teamId].includes(tag.id)) {
            state.workspaceTags[teamId].push(tag.id);
          }
        }
      }
    }
  },

  deleteLocalTag(state, { tagId }) {
    const tag = state.tags[tagId];
    if (tag.project_id) {
      state.projectTags[tag.project_id] ??= [];
      const index = state.projectTags[tag.project_id].indexOf(tagId);
      if (index > -1) {
        state.projectTags[tag.project_id].splice(index, 1);
      }
    } else {
      const index = state.workspaceTags[tag.team_id]?.indexOf?.(tagId);
      if (index > -1) {
        state.workspaceTags[tag.team_id].splice(index, 1);
      }
    }
    const newTags = { ...state.tags };
    delete newTags[tagId];
    state.tags = newTags;
  },

  addLocalTag(state, { tag }) {
    state.tags[tag.id] = tag;
    if (tag.project_id) {
      state.projectTags[tag.project_id] ??= [];
      state.projectTags[tag.project_id].push(tag.id);
    } else {
      state.workspaceTags[tag.team_id] ??= [];
      state.workspaceTags[tag.team_id].push(tag.id);
    }
  },

  updateLocalTag(state, { tagId, tag, cardId }) {
    if (state.tags[tagId]) {
      const updatedTag = { ...state.tags[tagId], ...tag };
      state.tags[tagId] = updatedTag;

      if (cardId) {
        // Single card update
        const card = state.cards[cardId];
        if (card?.tags?.length) {
          const tagIndex = card.tags.findIndex((t: any) => t.id === tagId);
          if (tagIndex > -1) {
            card.tags[tagIndex] = updatedTag;
          }
        }
      } else {
        // Update all cards that have this tag
        for (const cardId in state.cards) {
          const card = state.cards[cardId];
          if (!card?.tags?.length) continue;

          const tagIndex = card.tags.findIndex((t: any) => t.id === tagId);
          if (tagIndex > -1) {
            card.tags[tagIndex] = updatedTag;
          }
        }
      }
    }
  },

  removeLocalPropFromTag(state, { tagId, prop }) {
    if (state.tags[tagId]) {
      const newTag = { ...state.tags[tagId] };
      delete newTag[prop];
      state.tags[tagId] = newTag;
    }
  },

  updateLocalTagId(state, { localTagId, tag }) {
    if (tag.project_id) {
      state.projectTags[tag.project_id] ??= [];
      const index = state.projectTags[tag.project_id].indexOf(localTagId);
      if (index > -1) {
        state.projectTags[tag.project_id].splice(index, 1, tag.id);
      }
    } else {
      state.workspaceTags[tag.team_id] ??= [];
      const index = state.workspaceTags[tag.team_id].indexOf(localTagId);
      if (index > -1) {
        state.workspaceTags[tag.team_id].splice(index, 1, tag.id);
      }
    }
  },

  setCardCommentAttachmentUploadPopup(state, value) {
    state.cardCommentAttachmentUploadPopup = value;
  },

  setCardDescriptionAttachmentUploadPopup(state, value) {
    state.cardDescriptionAttachmentUploadPopup = value;
  },

  setLastOpenedBoardId(state, boardId = '') {
    state.lastOpenedBoardId = boardId;
  },

  setBoardList(state, { listId, list }) {
    state.lists[listId] = list;
  },

  removeBoardFromBoards(state, boardId) {
    delete state.flatBoards[boardId];
  },

  removeBoardsListsIds(state, boardId) {
    delete state.boardsListsIds[boardId];
  },

  lastHoveredListId(state, listId) {
    state.lastHoveredListId = listId;
  },

  lastHoveredCardId(state, cardId) {
    state.lastHoveredCardId = cardId;
  },

  lastHoveredDisplayedCardId(state, cardId) {
    state.lastHoveredDisplayedCardId = cardId;
  },

  setSelectedCardId(state, cardId) {
    state.selectedCardId = cardId;
  },

  setBoardViewMouseSuspended(state, value) {
    state.boardViewMouseSuspended = value;
  },

  setChecklistInputActive(state, { checklistId, isActive }) {
    const tempActiveChecklists = { ...state.checklistInputActive };
    if (!isActive) {
      delete tempActiveChecklists[checklistId];
      state.checklistInputActive = tempActiveChecklists;
    } else {
      state.checklistInputActive[checklistId] = isActive;
    }
  },
  setChecklistItemInputActive(state, { checklistItemId, isActive }) {
    const tempActiveChecklistItems = { ...state.checklistItemInputActive };
    if (!isActive) {
      delete tempActiveChecklistItems[checklistItemId];
      state.checklistItemInputActive = tempActiveChecklistItems;
    } else {
      state.checklistItemInputActive[checklistItemId] = isActive;
    }
  },
  setCardOwnerId(state, { ownerId, cardId }: { ownerId: string; cardId: string }) {
    state.cards[cardId].owner_id = ownerId;
  },

  setCardTemplates(state, templatesArray) {
    templatesArray.forEach((template: any) => {
      state.cardTemplates[template.id] = template;
    });
  },

  setCardTemplate(state, template) {
    state.cardTemplates[template.id] = template;
  },

  replaceCardTemplateId(state, { localId, template }) {
    delete state.cardTemplates[localId];
    state.cardTemplates[template.id] = template;
  },

  deleteCardTemplate(state, templateId) {
    delete state.cardTemplates[templateId];
  },

  setCardTemplateDraft(state, template) {
    const newTemplate = {
      card_board_id: template.card_board_id,
      card_content: template.card_content,
      card_list_id: template.card_list_id,
      card_members: template.card_members,
      card_priority: template.card_priority,
      card_tags: template.card_tags,
      card_title: template.card_title,
      card_child_cards: template.card_child_cards,
      icon: template.icon,
      project_ids: template.project_ids,
      title: template.title,
    };
    state.cardTemplateDraft = newTemplate;
  },

  setMembersToCardTemplate(state, value) {
    const { card_members } = state.cardTemplateDraft;
    let member = value;
    card_members.forEach((addedMember: any) => {
      if (value.user_id === addedMember.user_id) {
        card_members.splice(card_members.indexOf(addedMember), 1);
        member = {};
      }
    });
    if (member === value) card_members.push(value);
  },

  updateCardTemplateDraftField(state, { fieldName, value }) {
    state.cardTemplateDraft[fieldName] = value;
  },

  clearCardTemplateDraft(state) {
    state.cardTemplateDraft = {
      title: '',
      icon: {},
      project_ids: [],
      card_title: '',
      card_content: '',
      card_board_id: '',
      card_list_id: '',
      card_members: [],
      card_tags: [],
      card_priority: 0,
      card_child_cards: [],
    };
  },

  addTagToTemplate(state, tagId) {
    if (!state.cardTemplateDraft.card_tags.length) {
      state.cardTemplateDraft.card_tags?.push?.({ ids: [tagId] });
      return;
    }
    state.cardTemplateDraft.card_tags[0]?.ids?.push?.(tagId);
  },

  manageCardTemplateSelectedSpaces(state, spaceId) {
    const { project_ids } = state.cardTemplateDraft;
    const index = project_ids.indexOf(spaceId);
    if (index > -1) {
      project_ids.splice(index, 1);
    } else {
      project_ids.push(spaceId);
    }
  },

  toggleMemberTemplateChildCard(state, { index, member }) {
    const { card_child_cards } = state.cardTemplateDraft;
    if (!card_child_cards) return;
    const card = card_child_cards[index];
    if (!card) return;
    const memberIndex = card.members.findIndex((m: any) => m.user_id === member.user_id);
    if (memberIndex > -1) {
      card.members.splice(memberIndex, 1);
    } else {
      card.members.push(member);
    }
  },

  addChildCardToTemplate(state) {
    const { card_child_cards } = state.cardTemplateDraft;
    if (!card_child_cards) {
      state.cardTemplateDraft.card_child_cards = [];
    }
    const card = convertChildCardDraftToChildCard(state.quickChildCardObject);
    state.cardTemplateDraft.card_child_cards.push(card);
  },

  updateChildCardInTemplate(state, index) {
    const { card_child_cards } = state.cardTemplateDraft;
    if (!card_child_cards) return;
    const card = card_child_cards[index];
    if (!card) return;
    const newCard = convertChildCardDraftToChildCard(state.quickChildCardObject);
    card_child_cards[index] = newCard;
  },

  removeChildCardFromTemplate(state, index) {
    const { card_child_cards } = state.cardTemplateDraft;
    if (!card_child_cards) return;
    card_child_cards.splice(index, 1);
  },

  updateTemplateChildCardProperty(state, { fieldName, value, index }) {
    const { card_child_cards } = state.cardTemplateDraft;
    if (!card_child_cards) return;
    const card = card_child_cards[index];
    if (!card) return;
    card[fieldName] = value;
  },

  setTimelineZoom(state, zoom) {
    state.timelineZoom = zoom;
  },

  setTimelineSidebarWidth(state, width) {
    state.timelineSidebarWidth = width;
  },

  setCardWidth(state, width) {
    state.cardWidth = width;
  },

  closeCardUploadPopup(state) {
    state.cardCommentAttachmentUploadPopup = {};
    state.cardDescriptionAttachmentUploadPopup = {};
  },

  addQuickCardFileToUpdate(state, { file, isModal }) {
    const propertyName = getQuickCardFilesToUpdateName(isModal);
    state[propertyName].push(file);
  },

  removeQuickCardFilesToUpdate(state, isModal) {
    const propertyName = getQuickCardFilesToUpdateName(isModal);
    state[propertyName] = [];
  },

  setQuickCardTitle(state, { title, isModal }) {
    const propertyName = getQuickCardObjectName(isModal);
    state[propertyName].title = title;
  },

  setMembersToAddQuickCard(state, { value, isModal }) {
    const propertyName = getQuickCardObjectName(isModal);
    let member = value;
    state[propertyName].members.forEach((addedMember: any) => {
      if (value.user_id === addedMember.user_id) {
        state[propertyName].members.splice(state[propertyName].members.indexOf(addedMember), 1);
        member = {};
      }
    });
    if (member === value) state[propertyName].members.push(value);
  },

  setQuickCardField(state, { fieldName, value, isModal }) {
    const propertyName = getQuickCardObjectName(isModal);
    state[propertyName][fieldName] = value;
  },

  clearQuickCardObject(state, isModal) {
    const propertyName = getQuickCardObjectName(isModal);
    state[propertyName] = {
      localId: nanoid(6),
      title: '',
      content: '',
      tags: [],
      attachments: [],
      members: [],
      parentCard: {},
      projectId: '',
      priority: 0,
      board: {},
      hints: [],
      childCards: [],
    };
  },

  updateQuickCardChildCardProperty(state, { fieldName, value, index, isModal }) {
    const propertyName = getQuickCardObjectName(isModal);
    const { childCards } = state[propertyName];
    if (!childCards) return;
    const card = childCards[index];
    if (!card) return;
    card[fieldName] = value;
  },

  toggleMemberQuickCardChildCard(state, { index, member, isModal }) {
    const propertyName = getQuickCardObjectName(isModal);
    const { childCards } = state[propertyName];
    if (!childCards) return;
    const card = childCards[index];
    if (!card) return;
    const memberIndex = card.members.findIndex((m: any) => m.user_id === member.user_id);
    if (memberIndex > -1) {
      card.members.splice(memberIndex, 1);
    } else {
      card.members.push(member);
    }
  },

  setQuickCardProjectId(state, { projectId, isModal }) {
    const propertyName = getQuickCardObjectName(isModal);
    state[propertyName].projectId = projectId;
  },

  addTagToQuickCard(state, { tag, isModal }) {
    const propertyName = getQuickCardObjectName(isModal);
    state[propertyName].tags.push(tag);
  },

  removeTagFromQuickCard(state, { tagId, isModal }) {
    const propertyName = getQuickCardObjectName(isModal);
    const index = state[propertyName].tags.findIndex((t: any) => t.id === tagId);

    if (index >= 0) {
      state[propertyName].tags.splice(index, 1);
    }
  },

  addChildCardToQuickCard(state, isModal) {
    const propertyName = getQuickCardObjectName(isModal);
    const { childCards } = state[propertyName];
    if (!childCards) {
      state[propertyName].childCards = [];
    }
    const card = convertChildCardDraftToChildCard(state.quickChildCardObject);
    state[propertyName].childCards.push(card);
  },

  updateChildCardInQuickCard(state, { index, isModal }) {
    const propertyName = getQuickCardObjectName(isModal);
    const { childCards } = state[propertyName];
    if (!childCards) return;
    const card = childCards[index];
    if (!card) return;
    const newCard = convertChildCardDraftToChildCard(state.quickChildCardObject);
    childCards[index] = newCard;
  },

  removeChildCardFromQuickCard(state, { index, isModal }) {
    const propertyName = getQuickCardObjectName(isModal);
    const { childCards } = state[propertyName];
    if (!childCards) return;
    childCards.splice(index, 1);
  },

  setParentCardForQuickCard(state, parentCard) {
    state.quickCardObject.parentCard = parentCard;
  },

  setCardIdForMove(state, cardId) {
    state.cardIdForMove = cardId;
  },

  setBoardSortBy(state, { boardId, value, teamId }) {
    state.boardSortBy[boardId] = value;
    localStorage.setItem(`boardSortBy-${teamId}`, JSON.stringify(state.boardSortBy));
  },

  setBoardGroupBy(state, { boardId, value, teamId }) {
    state.boardGroupBy[boardId] = value;
    localStorage.setItem(`boardGroupBy-${teamId}`, JSON.stringify(state.boardGroupBy));
  },

  setChildCardCreation(state, value) {
    state.childCardCreation = value;
  },

  setCardInEpicCreation(state, value) {
    state.cardInEpicCreation = value;
  },

  setCardInNoteCreation(state, value) {
    state.cardInNoteCreation = value;
  },

  setQuickCardObject(state, { quickCardObject, isModal }) {
    const propertyName = getQuickCardObjectName(isModal);
    const quickCard = {
      ...quickCardObject,
      // add localId if it does not exist
      ...(!quickCardObject.localId && { localId: nanoid(6) }),
    };
    state[propertyName] = quickCard;
  },

  setQCSuggestions(state, suggestions) {
    state.quickCardObject.hints = suggestions;
  },

  setShowSuggestionsPlaceholder(state, showPlaceholder) {
    state.showSuggestionsPlaceholder = showPlaceholder;
  },
  setQCShowSuggestionsPlaceholder(state, showPlaceholder) {
    state.qcShowSuggestionsPlaceholder = showPlaceholder;
  },

  addGroupByList(state, { listId, groupBy, teamId }) {
    const boardIds = Object.keys(state.groupByListOrder);

    for (const bId of boardIds) {
      if (!state.groupByListOrder[bId]?.[groupBy]) continue;

      const existingList = state.groupByListOrder[bId][groupBy].find((id: any) => id === listId);

      if (!existingList) {
        state.groupByListOrder[bId][groupBy].push(listId);
      }
    }

    browserStorageSetItem(groupByListOrderKey(teamId), state.groupByListOrder);
  },

  removeGroupByList(state, { listId, groupBy, teamId }) {
    const boardIds = Object.keys(state.groupByListOrder);

    for (const bId of boardIds) {
      if (!state.groupByListOrder[bId]?.[groupBy]) continue;

      state.groupByListOrder[bId][groupBy].splice(
        state.groupByListOrder[bId][groupBy].indexOf(listId),
        1
      );

      if (groupBy === groupByOption.space && state.groupByListOrder[bId][groupBy].length === 0) {
        delete state.groupByListOrder[bId][groupBy];
      }

      browserStorageSetItem(groupByListOrderKey(teamId), state.groupByListOrder);
    }
  },

  replaceGroupByListCards(state, { boardId, listId, cards, groupBy, teamId }) {
    if (state.groupByCardOrder[boardId]?.[groupBy]?.[listId]) {
      state.groupByCardOrder[boardId][groupBy][listId] = cards;
    }

    browserStorageSetItem(groupByCardOrderKey(teamId), state.groupByCardOrder);
  },

  removeCardFromGroupByList(state, { boardId, epicId, cardId, listId, groupBy, teamId }) {
    const isSpecialCaseListId =
      (listId === 'notags' && groupBy === 'tag') ||
      (listId === 'unassigned' && groupBy === 'assignee') ||
      (listId === 'noproject' && groupBy === 'epic');
    const boardIds = [];

    if (boardId) {
      boardIds.push(boardId);
    }

    if (epicId) {
      boardIds.push(`${constants.inEpicBoardIdPrefix}-${epicId}`);
    }

    for (const bId of boardIds) {
      const lId = isSpecialCaseListId ? `${bId}-${listId}` : listId;

      if (!state.groupByCardOrder[bId]?.[groupBy]?.[lId]) continue;

      const index = state.groupByCardOrder[bId][groupBy][lId].findIndex((id: any) => id === cardId);

      if (index > -1) {
        state.groupByCardOrder[bId][groupBy][lId].splice(index, 1);
      }

      if (groupBy === groupByOption.space && state.groupByListOrder[bId]?.[groupBy]?.length === 0) {
        state.groupByListOrder[bId][groupBy] = state.groupByListOrder[bId][groupBy].filter(
          (id: any) => id !== lId
        );
        delete state.groupByListOrder[bId][groupBy];
      }

      browserStorageSetItem(groupByCardOrderKey(teamId), state.groupByCardOrder);
    }
  },

  updateGroupByListOrder(
    state,
    { boardId, listId, newIndex, oldIndex, groupBy, originalOrder, teamId }
  ) {
    state.groupByListOrder[boardId] ??= {};
    state.groupByListOrder[boardId][groupBy] ??= [...originalOrder];

    if (
      oldIndex < 0 ||
      oldIndex >= state.groupByListOrder[boardId][groupBy].length ||
      newIndex < 0 ||
      newIndex >= state.groupByListOrder[boardId][groupBy].length
    ) {
      return;
    }

    state.groupByListOrder[boardId][groupBy].splice(oldIndex, 1);
    state.groupByListOrder[boardId][groupBy].splice(newIndex, 0, listId);

    browserStorageSetItem(groupByListOrderKey(teamId), state.groupByListOrder);
  },

  setListCardsGroupByOrder(state, { groupByOrder, teamId }) {
    state.boardListGroupByOrder = { ...state.boardListGroupByOrder, ...groupByOrder };
    localStorage.setItem(`groupByOrder-${teamId}`, JSON.stringify(state.boardListGroupByOrder));
  },

  reorderCardsGroupByList(
    state,
    { boardId, listId, cardId, newIndex, oldIndex, groupBy, teamId, originalCardOrder }
  ) {
    state.groupByCardOrder[boardId] ??= {};
    state.groupByCardOrder[boardId][groupBy] ??= {};
    state.groupByCardOrder[boardId][groupBy][listId] ??= [];

    if (state.groupByCardOrder[boardId][groupBy][listId].length === 0 && originalCardOrder) {
      state.groupByCardOrder[boardId][groupBy][listId] = [...originalCardOrder];
    }

    state.groupByCardOrder[boardId][groupBy][listId].splice(oldIndex, 1);
    state.groupByCardOrder[boardId][groupBy][listId].splice(newIndex, 0, cardId);

    browserStorageSetItem(groupByCardOrderKey(teamId), state.groupByCardOrder);
  },

  updateCardsGroupByOrder(
    state,
    { boardId, originalListId, destinationListId, cardId, newIndex, groupBy, teamId }
  ) {
    if (!state.groupByCardOrder[boardId]?.[groupBy]) return;

    if (state.groupByCardOrder[boardId][groupBy][originalListId]) {
      const index = state.groupByCardOrder[boardId][groupBy][originalListId].indexOf(cardId);
      if (index > -1) {
        state.groupByCardOrder[boardId][groupBy][originalListId].splice(index, 1);
      }
    }

    if (state.groupByCardOrder[boardId][groupBy][destinationListId]) {
      const index = state.groupByCardOrder[boardId][groupBy][destinationListId].indexOf(cardId);
      if (index > -1) {
        state.groupByCardOrder[boardId][groupBy][destinationListId].splice(index, 1);
      }
      state.groupByCardOrder[boardId][groupBy][destinationListId].splice(newIndex, 0, cardId);
    }

    browserStorageSetItem(groupByCardOrderKey(teamId), state.groupByCardOrder);
  },

  setCurrentGroupByListIdCardActionEdit(state, listId) {
    state.currentGroupByListIdCardActionEdit = listId;
  },

  setCurrentCardUniqueRowId(state, uniqueRowId) {
    state.currentCardUniqueRowId = uniqueRowId;
  },

  setNewBoardObjectField(state, { fieldName, value }) {
    state.newBoardObject[fieldName] = value;
  },

  resetNewBoardObject(state) {
    state.newBoardObject = {};
  },

  setProjectSprintSettings(state, { projectId, settings }) {
    state.sprintSettings[projectId] = settings;
  },

  setSpaceSprints(state, { projectId, sprints }) {
    const oldSprints = state.spaceSprints[projectId] || {};
    const newSprints = { ...oldSprints, ...sprints };
    state.spaceSprints[projectId] = newSprints;
  },

  addSprintToSpaceSprints(state, { projectId, sprint }) {
    state.spaceSprints[projectId] ??= {};
    state.spaceSprints[projectId][sprint.id] = sprint;
  },

  setSprintListsFlat(state, lists) {
    const newSprintLists = { ...state.sprintLists, ...lists };
    state.sprintLists = newSprintLists;
  },

  setSprintForEdit(state, item) {
    state.sprintForEditId = item;
  },

  localUpdateSprint(state, { projectId, sprintId, prop, value }) {
    const sprint = state.spaceSprints[projectId][sprintId];
    if (sprint) {
      sprint[prop] = value;
    }
    state.spaceSprints[projectId][sprintId] = sprint;

    state.flatBoards[sprint.id] = { ...sprint };
  },

  setEpicSelectedTabId(state, { cardId, tabId }: { cardId: string; tabId: any }) {
    if (Object.values(EpicTabId).indexOf(tabId) === -1) {
      tabId = EpicTabId.Details;
    }
    state.epicSelectedTabId[cardId] = tabId;
  },

  setBoardSubGroupBy(state, { boardId, value, teamId }) {
    state.boardSubGroupBy[boardId] = value;
    browserStorageSetItem(boardSubGroupByKey(teamId), state.boardSubGroupBy);
  },

  setCardsSubGroupByOrder(state, { subGroupByBoardObject, teamId }) {
    const cardsSubGroupByOrder = { ...state.cardsSubGroupByOrder, ...subGroupByBoardObject };

    state.cardsSubGroupByOrder = cardsSubGroupByOrder;
    browserStorageSetItem(cardsSubGroupByOrderKey(teamId), state.cardsSubGroupByOrder);
  },

  setListsExpanded(state, { teamSlug, boardId, listId, value }) {
    state.listsExpanded ??= {};
    state.listsExpanded[boardId] ??= {};
    state.listsExpanded[boardId][listId] = value;

    const existingValues =
      (browserStorageGetItem(listsExpandedKey(teamSlug, boardId)) as Record<string, boolean>) ?? {};

    const newValues = { ...existingValues, [listId]: value };

    browserStorageSetItem(listsExpandedKey(teamSlug, boardId), newValues);
  },

  addCardToGroup(state, payload) {
    const { boardId, cardId, listId, groupBy, teamId, parentListId, subGroupBy, originalOrder } =
      payload;
    const isSubGroupCase = subGroupBy !== undefined && parentListId !== undefined;
    const getBoardIds = () => {
      const ids = [];
      if (boardId) ids.push(boardId);
      return ids;
    };

    const resolveListId = (bId: string) => {
      const specialCases = [
        listId === 'notags' && groupBy === 'tag',
        listId === 'unassigned' && groupBy === 'assignee',
        !isSubGroupCase && listId === 'noproject' && groupBy === 'epic',
      ];
      return specialCases.some(Boolean) ? `${bId}-${listId}` : listId;
    };

    const handleSubGroup = (bId: string, lId: string | number) => {
      const ensurePathExists = (obj: any, keys: string[], finalValue: any) => {
        let current = obj;
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i];
          if (!current[key]) {
            current[key] = i === keys.length - 1 ? finalValue : {};
          }
          current = current[key];
        }
      };

      const path = [bId, groupBy, subGroupBy, parentListId, lId];
      ensurePathExists(state.cardsSubGroupByOrder, path, { cardIds: [] });

      const target = path.reduce((acc, key) => acc[key], state.cardsSubGroupByOrder);
      if (!target.cardIds.includes(cardId)) {
        target.cardIds.push(cardId);
        localStorage.setItem(
          `subGroupByOrder-${teamId}`,
          JSON.stringify(state.cardsSubGroupByOrder)
        );
      }
    };

    const handleRegularGroup = (bId: string, lId: string | number) => {
      state.groupByCardOrder[bId] ??= {};
      state.groupByCardOrder[bId][groupBy] ??= {};
      state.groupByCardOrder[bId][groupBy][lId] ??= originalOrder;

      const group = state.groupByCardOrder[bId][groupBy];

      if (!group[lId].includes(cardId)) {
        group[lId].push(cardId);
        browserStorageSetItem(groupByCardOrderKey(teamId), state.groupByCardOrder);
      }
    };

    getBoardIds().forEach((bId: string) => {
      const lId = resolveListId(bId);

      isSubGroupCase ? handleSubGroup(bId, lId) : handleRegularGroup(bId, lId);
    });
  },
};

export default mutations;
